import { lookupSiteInfluenceAbbrev, lookupSiteViewAbbrev } from '@aloft/toolkit-shared';

export const RESIDENTIAL_SITE_VALUE = 'residential';

export type SiteComboboxOption = {
  value: string;
  label: string;
};

export type SiteComboboxRow =
  | { kind: 'option'; value: string; label: string }
  | { kind: 'custom'; value: string; label: string };

const normalize = (value: string) => value.trim().toLowerCase();

const abbreviationFor = (value: string): string | undefined =>
  lookupSiteInfluenceAbbrev(value)?.abbrev ?? lookupSiteViewAbbrev(value)?.abbrev;

const isExactMatch = (option: SiteComboboxOption, query: string): boolean => {
  const normalized = normalize(query);
  if (!normalized) return false;
  const abbreviation = abbreviationFor(option.value);
  return (
    normalize(option.label) === normalized ||
    normalize(option.value) === normalized ||
    (abbreviation !== undefined && normalize(abbreviation) === normalized)
  );
};

const matchesQuery = (option: SiteComboboxOption, query: string): boolean => {
  const normalized = normalize(query);
  if (!normalized) return true;
  const abbreviation = abbreviationFor(option.value);
  return (
    option.label.toLowerCase().includes(normalized) ||
    option.value.toLowerCase().includes(normalized) ||
    (abbreviation?.toLowerCase().includes(normalized) ?? false)
  );
};

const exactOption = (
  options: SiteComboboxOption[],
  query: string,
): SiteComboboxOption | undefined => options.find((option) => isExactMatch(option, query));

const pinResidential = (options: SiteComboboxOption[]): SiteComboboxOption[] => {
  const residential = options.find((option) => option.value === RESIDENTIAL_SITE_VALUE);
  if (!residential) return options;
  return [residential, ...options.filter((option) => option.value !== RESIDENTIAL_SITE_VALUE)];
};

export const filterSiteComboboxOptions = (
  options: SiteComboboxOption[],
  query: string,
): SiteComboboxOption[] => {
  const filtered = options.filter((option) => matchesQuery(option, query));
  const exact = exactOption(options, query);
  if (exact && filtered.some((option) => option.value === exact.value)) {
    return [exact, ...filtered.filter((option) => option.value !== exact.value)];
  }
  if (!query.trim()) return pinResidential(filtered);
  return filtered;
};

export const resolveSiteComboboxCommit = (
  options: SiteComboboxOption[],
  query: string,
): { value: string; custom: boolean } | undefined => {
  const trimmed = query.trim();
  if (!trimmed) return undefined;
  const exact = exactOption(options, trimmed);
  if (exact) return { value: exact.value, custom: false };
  return { value: trimmed, custom: true };
};

export const getSiteComboboxRows = (
  options: SiteComboboxOption[],
  query: string,
  allowCustom = true,
): SiteComboboxRow[] => {
  const rows: SiteComboboxRow[] = filterSiteComboboxOptions(options, query).map((option) => ({
    kind: 'option',
    value: option.value,
    label: option.label,
  }));
  const commit = resolveSiteComboboxCommit(options, query);
  if (allowCustom && commit?.custom) {
    rows.push({ kind: 'custom', value: commit.value, label: `Use "${commit.value}"` });
  }
  return rows;
};

export const siteComboboxClosedLabel = (
  options: SiteComboboxOption[],
  value?: string,
): string | undefined => {
  if (!value?.trim()) return undefined;
  return options.find((option) => option.value === value)?.label ?? value;
};
